import React from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydCheckbox from '../../../../commonDesign/SydCheckbox'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import { DEFAULT_ENDPOINTS, DEFAULT_POLICY, DEFAULT_TAG, AVAILABLE_COLUMNS, DEFAULT_COLUMNS } from './constants'

const styleVariantOptions = [
  { label: 'Small', value: 'sm' },
  { label: 'Medium (Default)', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

const arrowBtn = {
  background: 'none',
  border: '1px solid var(--color-neutral-200)',
  borderRadius: '4px',
  cursor: 'pointer',
  padding: '0 4px',
  fontSize: 'var(--font-size-xs)',
  lineHeight: '18px',
  color: 'var(--text-color-subtle)'
}

function WoSfTaskListConfigEditor ({ fieldPrefix }) {
  const { control } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.title`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Title' helperText='Override the default title'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Action Items'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.icon`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Icon' helperText='Icon name'>
              <IconPickerField
                value={field.value || null}
                onChange={field.onChange}
                placeholder='Select an icon...'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${fieldPrefix}.styleVariant`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Size' helperText='Component size variant'>
              <SydSelect
                options={styleVariantOptions}
                value={field.value || 'md'}
                onChange={(e) => field.onChange(e.target.value)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.showColumnHeaders`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show column headers'
              size='small'
              value={field.value === true}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Filtering</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.defaultStatusFilter`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Default Status Filter' helperText='Initial filter when component loads'>
              <SydSelect
                options={[
                  { label: 'Open (Default)', value: 'open' },
                  { label: 'Closed', value: 'closed' },
                  { label: 'All', value: 'all' }
                ]}
                value={field.value || 'open'}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.limit`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Limit' helperText='Max tasks to display (blank = all)'>
              <SydInput
                type='number'
                value={field.value ?? ''}
                onChange={(e) => {
                  const raw = e.target.value
                  if (!raw || !raw.trim()) { field.onChange(null); return }
                  const parsed = parseInt(raw, 10)
                  field.onChange(isNaN(parsed) ? null : parsed)
                }}
                placeholder='All'
              />
            </SydLabel>
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.columns`}
          control={control}
          render={({ field }) => {
            const raw = Array.isArray(field.value) && field.value.length ? field.value : DEFAULT_COLUMNS
            const entries = raw.map(e => typeof e === 'string' ? { key: e, header: null } : { key: e.key, header: e.header ?? null })
            const activeKeys = new Set(entries.map(e => e.key))

            const serialize = (list) => list.map(e => e.header ? { key: e.key, header: e.header } : e.key)

            const colMeta = {}
            AVAILABLE_COLUMNS.forEach(c => { colMeta[c.key] = c })

            const rows = [
              ...entries.map(e => ({ ...colMeta[e.key], ...e, enabled: true })),
              ...AVAILABLE_COLUMNS.filter(c => !activeKeys.has(c.key)).map(c => ({ ...c, header: null, enabled: false }))
            ]

            const toggle = (key) => {
              const next = activeKeys.has(key)
                ? entries.filter(e => e.key !== key)
                : [...entries, { key, header: null }]
              field.onChange(next.length ? serialize(next) : null)
            }
            const move = (key, dir) => {
              const idx = entries.findIndex(e => e.key === key)
              if (idx < 0) return
              const target = idx + dir
              if (target < 0 || target >= entries.length) return
              const next = [...entries]
              next[idx] = next[target]
              next[target] = entries[idx]
              field.onChange(serialize(next))
            }
            const setHeader = (key, header) => {
              const next = entries.map(e => e.key === key ? { ...e, header: header || null } : e)
              field.onChange(serialize(next))
            }

            return (
              <SydLabel label='Columns' helperText='Configure visible columns, ordering, and display names'>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '4px', fontSize: 'var(--font-size-xs)' }}>
                  <tbody>
                    {rows.map((row) => {
                      const activeIdx = entries.findIndex(e => e.key === row.key)
                      const isFirst = activeIdx === 0
                      const isLast = activeIdx === entries.length - 1
                      return (
                        <tr key={row.key} style={{ borderBottom: '1px solid var(--color-neutral-100)' }}>
                          <td style={{ width: '48px', padding: '4px 0', textAlign: 'center' }}>
                            {row.enabled ? (
                              <span style={{ display: 'inline-flex', gap: '2px' }}>
                                <button
                                  type='button'
                                  style={{ ...arrowBtn, ...(isFirst ? { visibility: 'hidden' } : {}) }}
                                  onClick={() => move(row.key, -1)}
                                >
                                  &uarr;
                                </button>
                                <button
                                  type='button'
                                  style={{ ...arrowBtn, ...(isLast ? { visibility: 'hidden' } : {}) }}
                                  onClick={() => move(row.key, 1)}
                                >
                                  &darr;
                                </button>
                              </span>
                            ) : null}
                          </td>
                          <td style={{ width: '32px', padding: '4px 0', textAlign: 'center' }}>
                            <SydCheckbox
                              size='small'
                              value={row.enabled}
                              onChange={() => toggle(row.key)}
                            />
                          </td>
                          <td style={{ padding: '4px 8px', color: row.enabled ? 'var(--text-color)' : 'var(--text-color-subtle)' }}>
                            {row.label}
                          </td>
                          <td style={{ padding: '4px 0', width: '120px' }}>
                            <input
                              type='text'
                              value={row.header || ''}
                              onChange={(e) => setHeader(row.key, e.target.value)}
                              disabled={!row.enabled}
                              placeholder={row.defaultHeader || '—'}
                              style={{
                                width: '100%',
                                fontSize: 'var(--font-size-xs)',
                                padding: '2px 6px',
                                border: '1px solid var(--color-neutral-200)',
                                borderRadius: '4px',
                                color: row.enabled ? 'var(--text-color)' : 'var(--text-color-subtle)',
                                backgroundColor: 'transparent',
                                opacity: row.enabled ? 1 : 0.5
                              }}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </SydLabel>
            )
          }}
        />
      </Grid>

      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.showHighPriorityBadge`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show high priority badge on subject'
              size='small'
              value={field.value !== false}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <div style={sectionHeading}>Smart Endpoint Overrides</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.listEndpoint`}
          control={control}
          render={({ field }) => (
            <SydLabel label='List Endpoint' helperText='SE base name for task list'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_ENDPOINTS.list}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.markEndpoint`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Mark Endpoint' helperText='SE base name for mark complete'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_ENDPOINTS.mark}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.policy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Policy' helperText='Production policy segment'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_POLICY}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.tag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Tag' helperText='Production tag segment'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder={DEFAULT_TAG}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.devPolicy`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Policy' helperText='Initial debug policy (defaults to Policy)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Policy'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.devTag`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Dev Tag' helperText='Initial debug tag (defaults to Tag)'>
              <SydInput
                {...field}
                value={field.value || ''}
                placeholder='Same as Tag'
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={`${configPrefix}.showDebugControl`}
          control={control}
          render={({ field }) => (
            <SydCheckbox
              label='Show debug control for Summit users (shown by default)'
              size='small'
              value={field.value !== false}
              onChange={(_e, checked) => field.onChange(checked)}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}

WoSfTaskListConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default WoSfTaskListConfigEditor
